import { ReconciliationCashDto } from '../../dto/report/reconciliation-cash.dto';
import { ReconciliationOrderDto } from '../../dto/report/reconciliation-order.dto';
import { PosDeviceTypeEnum } from '../../enum/pos-device-type.enum';
import {
  CashUpReviewEnum,
  ReconOverviewTypes,
  ReconciliationOrderReportTypeEnum
} from '../../enum/reconciliation';
import { ApplicationSourceEnum } from '../../dto/applicationSource.enum';
import { IPayment } from '../../interface/reconciliation/payment.interface';
import { IDepositsToRedeem } from '../../interface/reconciliation/deposit-to-redeem.interface';
import { IPaymentCurrencyBreakDown } from '../../interface/reconciliation/payment-currency-break-down.interface';
import { ITotalsBreakDown } from '../../interface/reconciliation/totals-break-down.interface';
import { SweetFaceTransactionType } from '../../enum';
import { SweetFaceTransactionDto } from '../../dto/sweet-face';

export class Reconciliation {
  overviewType: ReconOverviewTypes;
  id: number = null;
  name: string = null;
  isMultiCurrency = false;
  grossSales = 0;
  discount = 0;
  serviceCharge = 0;
  gratuities = 0;
  grossAfter = 0;
  giftCardSales = 0;
  depositsCollected = 0;
  totalGrossRevenue = 0;
  otherPayments: IPayment[] = [{
    paymentType: 'Petty Cash',
    amount: 0
  }];
  depositsToRedeem: IDepositsToRedeem = {
    redeemed: 0,
    outstanding: 0
  };
  integrations: IPayment[] = [];
  kiosk = 0;
  sweetFace = 0;
  totalToReconcile: IPaymentCurrencyBreakDown[] = [];
  totalReconciled: IPaymentCurrencyBreakDown[] = [];
  depositsUnRedeemed: IPaymentCurrencyBreakDown[] = [];
  variance: IPaymentCurrencyBreakDown[] = [];
  totalPos = 0;
  totalKiosk = 0;
  totalReconciliationSweetFace = 0;
  totalIntegrations = 0;
  cashPerTill: IPaymentCurrencyBreakDown[] = [];
  cashDeclared: IPaymentCurrencyBreakDown[] = [];
  cashVariance: IPaymentCurrencyBreakDown[] = [];
  cardPerTill: IPaymentCurrencyBreakDown[] = [];
  cardDeclared: IPaymentCurrencyBreakDown[] = [];
  cardVariance: IPaymentCurrencyBreakDown[] = [];

  constructor(
    reconOrder: ReconciliationOrderDto[],
    reconCashManagement: ReconciliationCashDto[],
    reconSweetFace: SweetFaceTransactionDto[],
    overviewType: ReconOverviewTypes,
    id: number,
    name: string
  ) {
    const usedCurrencies = this.extractCurrencies(reconOrder, reconCashManagement, reconSweetFace);
    this.overviewType = overviewType;
    this.id = id;
    this.name = name;
    // grouped by merchant and payment method
    // cash management reports are grouped by merchant and currency
    this.addOrderData(reconOrder);
    this.addCashManagementData(reconCashManagement);
    this.addSweetFaceTransactions(reconSweetFace);
    this.grossAfter = this.grossSales + this.serviceCharge + this.gratuities - this.discount;
    this.totalGrossRevenue = this.grossAfter + this.giftCardSales + this.depositsCollected;
    this.isMultiCurrency = usedCurrencies.size > 1;
    this.addTotals(usedCurrencies, reconOrder, reconCashManagement, reconSweetFace);
  }

  private extractCurrencies(
    reconOrder: ReconciliationOrderDto[],
    reconCashManagement: ReconciliationCashDto[],
    reconSweetFace: SweetFaceTransactionDto[]
  ): Set<string> {
    const usedCurrencies = new Set<string>();
    reconOrder.forEach(paymentByMethod => {
      if (paymentByMethod.reportName === ReconciliationOrderReportTypeEnum.RECONCILIATION) {
        usedCurrencies.add(`${paymentByMethod.paidCurrencyId};${paymentByMethod.paidCurrencySymbol};${paymentByMethod.paidCurrencyCode}`);
      }
    });
    reconCashManagement.forEach(cashByCurrency => {
      usedCurrencies.add(`${cashByCurrency.currencyId};${cashByCurrency.currencySymbol};${cashByCurrency.currencyCode}`);
    });
    reconSweetFace.forEach(sweetFaceByCurrency => {
      usedCurrencies.add(`${sweetFaceByCurrency.pieCurrency.currencyId};${sweetFaceByCurrency.pieCurrency.currencySymbol};${sweetFaceByCurrency.pieCurrency.currencyCode}`);
    });
    return usedCurrencies;
  }

  private addCashManagementData(
    reconCashManagement: ReconciliationCashDto[]
  ): void {
    reconCashManagement.forEach((cashByCurrency, index) => {
      if (cashByCurrency.currencyId === cashByCurrency.merchantMainCurrencyId) {
        this.gratuities += cashByCurrency.gratuities;
        this.giftCardSales += cashByCurrency.giftCardSales;
        this.depositsCollected += cashByCurrency.depositsCollected;
        this.depositsToRedeem.outstanding += cashByCurrency.depositsToRedeem;
        this.otherPayments[0].amount += cashByCurrency.pettyCash;
      }
    });
    this.depositsToRedeem.outstanding = this.depositsToRedeem.outstanding - this.depositsToRedeem.redeemed;
  }

  private addSweetFaceTransactions(reconSweetFace: SweetFaceTransactionDto[]) {
    let giftCardSweetFaceAmount = 0;
    let sweetFaceAmounts = 0;
    reconSweetFace.forEach(sweetFaceTransaction => {
      if (sweetFaceTransaction.transactionTypeName !== SweetFaceTransactionType.REFUND) {
        sweetFaceAmounts += sweetFaceTransaction.transactionAmount;
        if (sweetFaceTransaction.transactionTypeName === SweetFaceTransactionType.GIFT_CARD) {
          giftCardSweetFaceAmount += sweetFaceTransaction.transactionAmount;
        }
      }
    });

    this.sweetFace = sweetFaceAmounts;
    // add sweet face amount to all sales
    this.grossSales += sweetFaceAmounts;
    // add sweet face gift card amount to otherPayments
    const nonSweetFaceGiftCardAmounts = this.otherPayments.find(item => item.paymentType === 'Gift Card');
    if (nonSweetFaceGiftCardAmounts) {
      nonSweetFaceGiftCardAmounts.amount += giftCardSweetFaceAmount;
    } else {
      this.otherPayments.push({
        paymentType: 'Gift Card',
        amount: giftCardSweetFaceAmount
      });
    }
  }

  private addOrderData(reconOrder: ReconciliationOrderDto[]): void {
    reconOrder.forEach((paymentByMethod, index) => {
      if (paymentByMethod.reportName === ReconciliationOrderReportTypeEnum.RECONCILIATION) {
        // calculate and sum gross sales: settled amount plus discount minus service charge and fees
        this.grossSales += paymentByMethod.settledAmount
          + paymentByMethod.discountAmount
          - paymentByMethod.serviceChargeAmount
          - paymentByMethod.deliveryFee
          - paymentByMethod.processingFee;
        // sum discount and service charge
        this.discount += paymentByMethod.discountAmount;
        this.serviceCharge += paymentByMethod.serviceChargeAmount;
        // sum redeemed deposits
        if (paymentByMethod.isDepositPayment) {
          this.depositsToRedeem.redeemed += this.calculatePaymentWoFees(paymentByMethod);
        }
        //group and sum other payments
        if (paymentByMethod.isOtherPayment) {
          this.groupAndSumOtherPaymentsByType(paymentByMethod);
        }
        //group and sum integrations
        if (paymentByMethod.isIntegration) {
          this.groupAndSumIntegrationsByType(paymentByMethod);
        }
        // sum redeemed kiosk
        if (this.isKioskPayment(paymentByMethod)) {
          this.kiosk += this.calculatePaymentWoFees(paymentByMethod);
        }
      }
    });
  }

  private isKioskPayment(paymentByMethod: ReconciliationOrderDto): boolean {
    return paymentByMethod.paymentType === 'Card Kiosk (UpCo Kiosk)'
      || paymentByMethod.posDeviceTypeId === PosDeviceTypeEnum.KIOSK;
  }

  private calculatePaymentWoFees(paymentByMethod: ReconciliationOrderDto): number {
    return paymentByMethod.settledAmount -
      paymentByMethod.deliveryFee -
      paymentByMethod.processingFee;
  }

  private extractPaymentToReconcile(paymentByMethod: ReconciliationOrderDto): number {
    // only cash payments will be reconciled in originally paid currency
    return paymentByMethod.fxCurrencyAmount > 0 && paymentByMethod.isCash ?
      paymentByMethod.fxCurrencyAmount : this.calculatePaymentWoFees(paymentByMethod);
  }

  private groupAndSumIntegrationsByType(paymentByMethod: ReconciliationOrderDto): void {
    const existing = this.integrations.find(payment => payment.paymentType.trim() === paymentByMethod.paymentType.trim());
    if (!existing) {
      this.integrations.push({
        paymentType: paymentByMethod.paymentType.trim(),
        amount: this.calculatePaymentWoFees(paymentByMethod)
      });
    } else {
      existing.amount += this.calculatePaymentWoFees(paymentByMethod);
    }
  }

  private groupAndSumOtherPaymentsByType(paymentByMethod: ReconciliationOrderDto): void {
    const existing = this.otherPayments.find(payment => payment.paymentType.trim() === paymentByMethod.paymentType.trim());
    if (!existing) {
      this.otherPayments.push({
        paymentType: paymentByMethod.paymentType.trim(),
        amount: this.calculatePaymentWoFees(paymentByMethod)
      });
    } else {
      existing.amount += this.calculatePaymentWoFees(paymentByMethod);
    }
  }

  private addTotals(
    usedCurrencies: Set<string>,
    reconOrder: ReconciliationOrderDto[],
    reconCashManagement: ReconciliationCashDto[],
    reconSweetFace: SweetFaceTransactionDto[]
  ): void {

    // generate totalToReconcile, totalReconciled, depositsUnRedeemed and variance by currency
    usedCurrencies.forEach(usedCurrency => {

      const [currencyIdStr, currencySymbol, currencyCode] = usedCurrency.split(';');
      const currencyId = parseInt(currencyIdStr, 10);

      // FOR EVERY USED CURRENCY
      // calculate totals
      this.calculateTotals(currencyId, currencySymbol, currencyCode, reconOrder, reconCashManagement, reconSweetFace);
      // calculate totals for cards
      this.calculateCardsPerTill(currencyId, currencySymbol, currencyCode, reconOrder, reconCashManagement, reconSweetFace);
      // calculate totals for cash
      this.calculateCashPerTill(currencyId, currencySymbol, currencyCode, reconOrder, reconCashManagement, reconSweetFace);
    });

    // generate totalPos, totalKiosk, totalIntegrations
    this.totalPos = reconOrder
      .filter(item => this.isToReconcilePayment(item))
      .reduce((prev, curr) => prev + this.calculatePaymentWoFees(curr), 0);

    this.totalKiosk = reconOrder
      .filter(item => this.isKioskPayment(item))
      .reduce((prev, curr) => prev + this.calculatePaymentWoFees(curr), 0);

    this.totalReconciliationSweetFace = reconSweetFace
      .filter(item =>
        item.transactionTypeName !== SweetFaceTransactionType.GIFT_CARD
        && item.transactionTypeName !== SweetFaceTransactionType.REFUND
      )
      .reduce((prev, curr) => prev + curr.transactionAmount, 0);

    this.totalIntegrations = reconOrder
      .filter(item => item.isIntegration)
      .reduce((prev, curr) => prev + this.calculatePaymentWoFees(curr), 0);
  }

  private calculateTotals(
    currencyId: number,
    currencySymbol: string,
    currencyCode: string,
    reconOrder: ReconciliationOrderDto[],
    reconCashManagement: ReconciliationCashDto[],
    reconSweetFace: SweetFaceTransactionDto[]
  ): void {
    const cashUpSweetFace = reconSweetFace
      .filter(record => record.transactionTypeName === SweetFaceTransactionType.CARD ||
        record.transactionTypeName === SweetFaceTransactionType.CASH);

    const {
      totalToReconcile,
      totalCounted,
      depositsUnRedeemed,
      variance
    } = this.calculateTotalsBreakDown(currencyId, reconOrder, reconCashManagement, cashUpSweetFace, CashUpReviewEnum.TOTAL);

    if (totalToReconcile !== 0) {
      this.totalToReconcile.push({
        amount: totalToReconcile,
        currencyId: currencyId,
        currencySymbol,
        currencyCode,
      });
    }

    if (totalCounted !== 0) {
      this.totalReconciled.push({
        amount: totalCounted,
        currencyId: currencyId,
        currencySymbol,
        currencyCode,
      });
    }

    if (depositsUnRedeemed !== 0) {
      this.depositsUnRedeemed.push({
        amount: depositsUnRedeemed,
        currencyId: currencyId,
        currencySymbol,
        currencyCode,
      });
    }
    if (totalToReconcile !== 0 || totalCounted !== 0 || depositsUnRedeemed !== 0) {
      this.variance.push({
        amount: variance,
        currencyId: currencyId,
        currencySymbol,
        currencyCode,
      });
    }
  }

  private calculateCardsPerTill(
    currencyId: number,
    currencySymbol: string,
    currencyCode: string,
    reconOrder: ReconciliationOrderDto[],
    reconCashManagement: ReconciliationCashDto[],
    reconSweetFace: SweetFaceTransactionDto[]
  ): void {
    const reconOrderCard = reconOrder.filter(record => this.isToReconcilePayment(record) && !record.isCash);
    const cashUpCard = reconCashManagement.filter(record => record.cardCashUpTotal >= 0);
    const cashUpSweetFace = reconSweetFace.filter(record => record.transactionTypeName === SweetFaceTransactionType.CARD);
    const {
      totalToReconcile,
      totalCounted,
      variance
    } = this.calculateTotalsBreakDown(currencyId, reconOrderCard, cashUpCard, cashUpSweetFace, CashUpReviewEnum.CARD);
    if (totalToReconcile !== 0) {
      this.cardPerTill.push({
        amount: totalToReconcile,
        currencyId: currencyId,
        currencySymbol,
        currencyCode,
      });
    }

    if (totalCounted !== 0) {
      this.cardDeclared.push({
        amount: totalCounted,
        currencyId: currencyId,
        currencySymbol,
        currencyCode,
      });
    }

    if (totalToReconcile !== 0 || totalCounted !== 0) {
      this.cardVariance.push({
        amount: variance,
        currencyId: currencyId,
        currencySymbol,
        currencyCode,
      });
    }
  }

  private calculateCashPerTill(
    currencyId: number,
    currencySymbol: string,
    currencyCode: string,
    reconOrder: ReconciliationOrderDto[],
    reconCashManagement: ReconciliationCashDto[],
    reconSweetFace: SweetFaceTransactionDto[]
  ): void {
    const reconOrderCard = reconOrder.filter(record => record.isCash);
    const cashUpCard = reconCashManagement.filter(record => record.cashCashUpTotal >= 0);
    const cashUpSweetFace = reconSweetFace.filter(record => record.transactionTypeName === SweetFaceTransactionType.CASH);

    const {
      totalToReconcile,
      totalCounted,
      variance
    } = this.calculateTotalsBreakDown(currencyId, reconOrderCard, cashUpCard, cashUpSweetFace, CashUpReviewEnum.CASH);
    if (totalToReconcile !== 0) {
      this.cashPerTill.push({
        amount: totalToReconcile,
        currencyId: currencyId,
        currencySymbol,
        currencyCode,
      });
    }

    if (totalCounted !== 0) {
      this.cashDeclared.push({
        amount: totalCounted,
        currencyId: currencyId,
        currencySymbol,
        currencyCode,
      });
    }

    if (totalToReconcile !== 0 || totalCounted !== 0) {
      this.cashVariance.push({
        amount: variance,
        currencyId: currencyId,
        currencySymbol,
        currencyCode,
      });
    }
  }

  private calculateTotalsBreakDown(
    currencyId: number,
    reconOrder: ReconciliationOrderDto[],
    reconCashManagement: ReconciliationCashDto[],
    reconSweetFace: SweetFaceTransactionDto[],
    cashUpReviewType: CashUpReviewEnum
  ): ITotalsBreakDown {
    // sum all the payments
    const receivedPayments = reconOrder
      .filter(orderPayment => orderPayment.paidCurrencyId === currencyId)
      .reduce((prev, curr) => prev + this.extractPaymentToReconcile(curr), 0);
    // sum all the integration, kiosk and other payments as 'other payments'
    const otherPayments = reconOrder
      .filter(orderPayment => orderPayment.paidCurrencyId === currencyId && !this.isToReconcilePayment(orderPayment))
      .reduce((prev, curr) => prev + this.extractPaymentToReconcile(curr), 0);
    // find cash management reports by currency
    const currencyCashReports = reconCashManagement.filter(byCurrency => byCurrency.currencyId === currencyId);
    // find sweet face transactions by currency
    const currencySweetFaceTransactions = reconSweetFace.filter(byCurrency => byCurrency.pieCurrency.currencyId === currencyId);

    // sum received cash other than order payments
    const receivedCash = currencyCashReports
      .reduce((prev, curr) => prev +
        (curr.gratuities || 0) +
        (curr.depositsCollected || 0) +
        ((cashUpReviewType === CashUpReviewEnum.CARD) || (cashUpReviewType === CashUpReviewEnum.TOTAL) ? (curr.giftCardSales || 0) : 0), 0);
    // sum Sweet Face Payments
    const totalSweetFace = currencySweetFaceTransactions
      .reduce((prev, curr) => prev + (curr.transactionAmount || 0), 0);
    // total received money are all the payments plus gratuities plus collected deposits
    const receivedMoney = receivedPayments + receivedCash + totalSweetFace;

    // sum counted money (the last count from Cash Up)
    const totalCounted = currencyCashReports
      .reduce((prev, curr) => prev +
        (cashUpReviewType === CashUpReviewEnum.TOTAL || cashUpReviewType === CashUpReviewEnum.CARD ? (curr.cardCashUpTotal || 0) : 0) +
        (cashUpReviewType === CashUpReviewEnum.TOTAL || cashUpReviewType === CashUpReviewEnum.CASH ? (curr.cashCashUpTotal || 0) : 0), 0
      );

    // sum deposits to redeem
    const totalDepositsToRedeem = currencyCashReports
      .reduce((prev, curr) => prev + (curr.depositsToRedeem || 0), 0);

    // sum petty cash
    const totalPettyCash = currencyCashReports
      .reduce((prev, curr) => prev + (curr.pettyCash || 0), 0);

    // calculate total to reconcile -> total received money minus petty cash minus other payments minus kiosk
    const totalToReconcile = receivedMoney - (cashUpReviewType === CashUpReviewEnum.CARD ? 0 : totalPettyCash) - otherPayments;

    // calculate deposits unredeemed -> deposits to be redeemed during selected day minus payments as isDepositPayment
    const totalDepositsRedeemed = reconOrder
      .filter(orderPayment => orderPayment.isDepositPayment && orderPayment.paidCurrencyId === currencyId)
      .reduce((prev, curr) => prev + this.extractPaymentToReconcile(curr), 0);
    const depositsUnRedeemed = this.calculateDepositsUnRedeemed(totalDepositsToRedeem, totalDepositsRedeemed);

    // calculate variance -> total reconciled plus deposits unredeemed minus total to reconcile
    const variance = this.calculateVariance(totalCounted, totalToReconcile, depositsUnRedeemed);

    return {totalToReconcile, totalCounted, depositsUnRedeemed, variance};
  }

  private isToReconcilePayment(payment: ReconciliationOrderDto): boolean {
    return !payment.isIntegration &&
      !payment.isOtherPayment &&
      !this.isKioskPayment(payment) &&
      this.isToReconcileApplicationSource(payment)
  }

  private isToReconcileApplicationSource(paymentType: ReconciliationOrderDto): boolean {
    return paymentType.applicationSourceId === ApplicationSourceEnum.WEB_POS
      || paymentType.applicationSourceId === ApplicationSourceEnum.VIRTUAL_POS;
  }

  private calculateVariance(totalReconciled = 0, totalToReconcile = 0, depositsUnredeemed = 0): number {
    return totalReconciled + depositsUnredeemed - totalToReconcile;
  }

  private calculateDepositsUnRedeemed(depositsToRedeem = 0, depositsRedeemed = 0): number {
    return depositsToRedeem - depositsRedeemed;
  }
}
